import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import type { ProjectFileResponseDto } from '@inference.engine/workspace-api-client/web';
import { ROUTES } from '@/shared/config/routes';

interface UseAutoSelectFirstSiblingArgs {
  projectId: string;
  folderId: string | undefined;
  fileId: string | undefined;
  siblingFiles: ProjectFileResponseDto[];
  isSiblingFilesLoading: boolean;
}

/**
 * When the filtered sibling list no longer contains the file currently in
 * the route (e.g. after the user toggles the filmstrip status filter), jump
 * to the first match so the user isn't stuck previewing a hidden file.
 *
 * No-ops when:
 *   - the sibling query is still loading (avoid acting on stale `[]`),
 *   - the list is empty (let the caller render an empty state),
 *   - the current file is already in the list.
 *
 * Uses `replace` so the back button still escapes the asset view in one step,
 * and preserves any active `?status=…` (or other) search params.
 */
export function useAutoSelectFirstSibling({
  projectId,
  folderId,
  fileId,
  siblingFiles,
  isSiblingFilesLoading,
}: UseAutoSelectFirstSiblingArgs): void {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (isSiblingFilesLoading) return;
    if (!fileId || !folderId) return;
    if (siblingFiles.length === 0) return;
    if (siblingFiles.some((f) => f.id === fileId)) return;

    void navigate(
      {
        pathname: ROUTES.PROJECT_FILE_DETAIL(projectId, folderId, siblingFiles[0].id),
        search: searchParams.toString(),
      },
      { replace: true },
    );
  }, [fileId, folderId, isSiblingFilesLoading, siblingFiles, navigate, projectId, searchParams]);
}
